/**
 * Data layer for `creative-size-limit`. Thin typed fetch wrappers over
 * /api/v1/creative-size-limit. Raw `fetch()` is confined to this file per
 * the dash contract.
 *
 * BE endpoints (tenant-scoped):
 *   GET    /api/v1/creative-size-limit            -> effective ceiling
 *   PUT    /api/v1/creative-size-limit            -> admin override {max_bytes, overridable?}
 *   DELETE /api/v1/creative-size-limit            -> reset to platform default
 *   POST   /api/v1/creative-size-limit/check      -> preflight {size_bytes, mime?}
 */

export interface EffectiveLimitView {
	tenant_id: string;
	max_bytes: number;
	overridable: boolean;
	is_default: boolean;
	label: string;
	default_max_bytes: number;
	min_max_bytes: number;
	max_max_bytes: number;
}

export interface UpdateLimitBody {
	max_bytes: number;
	overridable?: boolean;
}

export interface UpdatedLimitView {
	tenant_id: string;
	max_bytes: number;
	overridable: boolean;
	is_default: boolean;
	label: string;
	updated_by: string | null;
	updated_at: number;
}

export interface ResetLimitView {
	tenant_id: string;
	max_bytes: number;
	overridable: boolean;
	is_default: boolean;
	label: string;
}

export interface CheckLimitBody {
	size_bytes: number;
	mime?: string;
}

export type CheckReason =
	| "too_large"
	| "negative_size"
	| "invalid_size"
	| "mime_not_allowed";

export interface CheckLimitOk {
	ok: true;
	max_bytes: number;
	remaining_bytes: number;
	tenant_id: string;
	is_default: boolean;
	label: string;
}

export interface CheckLimitFail {
	ok: false;
	max_bytes: number;
	reason: CheckReason;
	tenant_id: string;
	is_default: boolean;
	label: string;
}

export type CheckLimitResponse = CheckLimitOk | CheckLimitFail;

const BASE = "/api/v1/creative-size-limit";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function getEffective(): Promise<EffectiveLimitView> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<EffectiveLimitView>(res);
}

export async function updateLimit(
	body: UpdateLimitBody,
): Promise<UpdatedLimitView> {
	const res = await fetch(BASE, {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<UpdatedLimitView>(res);
}

export async function resetLimit(): Promise<ResetLimitView> {
	const res = await fetch(BASE, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ResetLimitView>(res);
}

export async function checkLimit(
	body: CheckLimitBody,
): Promise<CheckLimitResponse> {
	const res = await fetch(`${BASE}/check`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CheckLimitResponse>(res);
}

export const creativeSizeLimitQueryKeys = {
	all: ["creative-size-limit"] as const,
	effective: () => [...creativeSizeLimitQueryKeys.all, "effective"] as const,
};
